import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import * as Promise from 'bluebird';
import * as propz from 'propz';
import { Component } from 'react';
import {
  ColumnDefinition,
  getFilters,
  getOrder,
  getSearchFilter,
  getSearchOrder,
  getServerFieldSectionWhere,
  getServerQueryFilter,
  getPagesCount,
  isFilterExist,
  isSortExist,
  TABLE_SORT_DIRECTION,
} from 'Src/helpers/table';
import { DATE_INTERVAL, FILTER_TYPE, FIRST_PAGE } from 'Src/consts/table';
import { parse } from 'query-string';
import { Loader } from 'Src/components/Loader/Loader';
import { Grid } from 'Src/components/Grid/Grid';
import { History, Location } from 'history';
import { SimpleModal } from '../../../../../../../components/SimpleModal/SimpleModal';
import { Order } from '../../../../../../../models/orders';
import { getOrderById } from '../../../../../../../services/superadmin/orders';
import {
  addOrderImage,
  deleteOrderImage,
  getAllOrderImages,
  getOrderImages,
  getOrderImagesCount,
  updateOrderImage,
} from '../../../../../../../services/superadmin/orderImages';
import { OrderImage } from '../../../../../../../models/orderImages';
import { OrderImageForm } from './OrderImageForm/OrderImageForm';
import { convertToFilterWithPlus } from '../../../../../../../helpers/table';
import { LIMIT } from '../../../../../../../consts/table';
import { getIncludedInReprint } from '../../../../../../../helpers/accessor';
import { GenericSummary } from '../../../../../../../components/GenericSummary/GenericSummary';
import { OrderImageSummary } from './OrderImageSummary/OrderImageSummary';
import { IMAGE_TABS } from '../../../../../../../consts/images';
import { ResizablePanel } from '../../../../../../../components/ResizablePanel/ResizablePanel';
import { TABS } from '../../../../../../../consts/common';

const COLUMNS: ColumnDefinition[] = [
  {
    text: 'Image filename',
    field: 'imageFileName',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['image', 'fileName'],
  },
  {
    text: 'Package',
    field: 'packageName',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['package', 'name'],
  },
  {
    text: 'Product',
    field: 'productName',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['product', 'name'],
  },
  {
    text: 'Price set',
    field: 'priceSetName',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['priceSet', 'name'],
  },
  {
    text: 'Quantity',
    field: 'quantity',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: ['quantity'],
  },
  {
    text: 'Included in reprint',
    field: 'isIncludedInReprint',
    isSort: false,
    type: FILTER_TYPE.NONE,
    accessor: getIncludedInReprint,
  },
];

interface State {
  items: OrderImage[];
  currentPage: number;
  selectedItems: OrderImage[];
  sortDirection: TABLE_SORT_DIRECTION;
  sortColumnsName: string;
  isShowFilter: boolean;
  isDataFiltered: boolean;
  filters: any;
  isLoading: boolean;
  isSelectAllChecked: boolean;
  total: number;
  basePath: string;
  order: Order;
  isConfirmationModalOpen: boolean;
  isOrderImageFormModalOpen: boolean;
  currentTabIndex: number;
  isErrorModalOpen: boolean;
  errorMessage: string;
}

interface Props {
  user: AppUser;
  history: History;
  location: Location;
}

export class OrderImages extends Component<Props, State> {
  constructor(props) {
    super(props);

    this.state = {
      items: [],
      currentPage: FIRST_PAGE,
      selectedItems: [],
      sortDirection: '',
      sortColumnsName: '',
      isShowFilter: false,
      isDataFiltered: false,
      filters: {},
      isLoading: false,
      isSelectAllChecked: false,
      total: 0,
      basePath: '',
      order: undefined,
      isConfirmationModalOpen: false,
      isOrderImageFormModalOpen: false,
      currentTabIndex: 0,
      isErrorModalOpen: false,
      errorMessage: '',
    };
  }

  getOrderId(): string {
    const { history } = this.props;
    const search = parse(history.location.search);
    const orderId = search.orderId as string;
    return orderId;
  }

  componentDidMount() {
    this.setState({
      isLoading: true,
    });

    this.setAdditionalItems().then(res => {
      this.setItems();
    });
  }

  setAdditionalItems() {
    const { user } = this.props;
    const orderId = this.getOrderId();

    return getOrderById(user, orderId).then(order => {
      this.setState({
        order: order,
      });

      return true;
    });
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    if (nextProps.location.search !== this.props.location.search) {
      this.setState({
        isLoading: true,
      });
      this.setItems();
    }
  }

  setItems() {
    const { history, user } = this.props;

    const search = parse(history.location.search);
    const page = typeof search.page !== 'undefined' ? Number(search.page) : FIRST_PAGE;

    let sortDirection = '';
    let sortColumnsName = '';

    if (typeof search.order === 'string') {
      [sortColumnsName, sortDirection] = search.order.split(':');
    }

    const sortByFieldExist = sortColumnsName !== '';
    const sortDirectionExist = sortDirection !== '';

    const filters = getFilters(COLUMNS, search);

    const isShowFilter = isFilterExist(filters);
    const isDataFiltered = isFilterExist(filters);

    const where = getServerFieldSectionWhere(filters);

    const order = sortByFieldExist && sortDirectionExist ? `${sortColumnsName} ${sortDirection}` : undefined;
    const serverQueryFilter = getServerQueryFilter(page, where, order);

    const orderId = this.getOrderId();

    const getItemsPromise = getOrderImages(user, orderId, serverQueryFilter);
    const getItemsCountPromise = getOrderImagesCount(user, orderId, where);

    const promises = [getItemsCountPromise, getItemsPromise];

    return Promise.all(promises).then(([countObj, items]) => {
      this.setState({
        currentPage: page,
        sortDirection: sortDirection as TABLE_SORT_DIRECTION,
        sortColumnsName: sortColumnsName,
        isShowFilter: isShowFilter,
        isDataFiltered: isDataFiltered,
        filters: filters,
        items: items,
        total: countObj.count,
        basePath: history.location.pathname,
        isLoading: false,
      });

      return true;
    });
  }

  setCurrentPageParams = (currentPage: number): void => {
    let search = [];

    const { filters, sortColumnsName, sortDirection } = this.state;

    if (currentPage !== 1) {
      search.push(`page=${currentPage}`);
    }

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const orderId = this.getOrderId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `orderId=${orderId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onItemClick = (index: number): void => {
    const { items, selectedItems } = this.state;
    const selectedItem = items[index];

    const selectedItemIndex = selectedItems.findIndex(item => selectedItem.id === item.id);
    let selectedItemsUpdated = [...selectedItems];

    if (selectedItemIndex !== -1) {
      selectedItemsUpdated.splice(selectedItemIndex, 1);
    } else {
      selectedItemsUpdated.push(selectedItem);
    }

    this.setState({
      selectedItems: selectedItemsUpdated,
    });
  };

  onSelectAllOnPageClick = (): void => {
    const { items, selectedItems } = this.state;

    const itemsFiltered = items.filter(item => {
      return selectedItems.every(selectedItem => selectedItem.id !== item.id);
    });

    const selectedItemsNext = [...selectedItems, ...itemsFiltered];

    this.setState({
      selectedItems: selectedItemsNext,
      isSelectAllChecked: true,
    });
  };

  onSelectAllClick = (): void => {
    const { selectedItems, filters } = this.state;

    this.setState({
      isLoading: true,
    });

    const user = this.props.user;

    const where = getServerFieldSectionWhere(filters);

    const orderId = this.getOrderId();

    getAllOrderImages(user, orderId, where).then(items => {
      const itemsFiltered = items.filter(item => {
        return selectedItems.every(selectedItem => selectedItem.id !== item.id);
      });

      const selectedItemsNext = [...selectedItems, ...itemsFiltered];

      this.setState({
        selectedItems: selectedItemsNext,
        isSelectAllChecked: true,
        isLoading: false,
      });
    });
  };

  onUnselectAllClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });
  };

  onTableSortClick = (sortField: string): void => {
    const { sortDirection, sortColumnsName, filters } = this.state;

    const order = getOrder(sortField, sortDirection, sortColumnsName);

    let search = [];
    search.push(`order=${sortField}:${order}`);

    const isFilter = isFilterExist(filters);

    if (isFilter) {
      search.push(getSearchFilter(filters));
    }

    const orderId = this.getOrderId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `order=${orderId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onTableFilterChange = (event, filterField: string, options?): void => {
    const filterValue = event.target.value;
    const filters = this.state.filters;
    const currentFilterField = filters[filterField];

    let nextFilters = { ...filters };

    if (typeof options !== 'undefined') {
      switch (options) {
        case DATE_INTERVAL.FROM:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              from: filterValue,
            },
          };
          break;
        case DATE_INTERVAL.TO:
          nextFilters = {
            ...nextFilters,
            [filterField]: {
              ...currentFilterField,
              to: filterValue,
            },
          };
          break;
      }
    } else {
      const filter = COLUMNS.find(col => col.field === filterField);
      const filterType = filter.type;

      if (filterType === FILTER_TYPE.MULTISELECT) {
        const options = event.target.options;
        const value = [];
        for (let i = 0; i < options.length; i++) {
          if (options[i].selected) {
            value.push(options[i].value);
          }
        }
        nextFilters = {
          ...nextFilters,
          [filterField]: value,
        };
      } else {
        nextFilters = {
          ...nextFilters,
          [filterField]: filterValue,
        };
      }
    }

    this.setState({
      filters: nextFilters,
    });
  };

  onApplyFilterClick = (): void => {
    const { filters, sortDirection, sortColumnsName } = this.state;
    let search = [];

    if (isSortExist(sortDirection, sortColumnsName)) {
      search.push(getSearchOrder(sortDirection, sortColumnsName));
    }

    const isFilter = isFilterExist(filters);

    const filtersConverted = convertToFilterWithPlus(filters);

    if (isFilter) {
      search.push(getSearchFilter(filtersConverted));
    }

    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    const orderId = this.getOrderId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `order=${orderId}&${search.join('&')}`,
      state: history.location.state,
    });
  };

  onClearFilterClick = (): void => {
    this.setState({
      selectedItems: [],
      isSelectAllChecked: false,
    });

    const orderId = this.getOrderId();

    const { history } = this.props;

    this.props.history.push({
      pathname: this.state.basePath,
      search: `order=${orderId}`,
      state: history.location.state,
    });
  };

  onTableFilterClick = (event): void => {
    event.preventDefault();

    const isShowFilter = this.state.isShowFilter;

    this.setState({
      isShowFilter: !isShowFilter,
    });
  };

  goBack = () => {
    const { history, location } = this.props;
    const { state } = location;
    const { search } = state as any;
    const orderId = this.getOrderId();

    history.push({
      pathname: '/orders',
      search: search,
      state: { orderId: orderId },
    });
  };

  showConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: true,
    });
  };

  hideConfirmationModal = () => {
    this.setState({
      isConfirmationModalOpen: false,
    });
  };

  renderConfirmationModal(): React.ReactNode {
    const { isConfirmationModalOpen, selectedItems } = this.state;

    return (
      <SimpleModal
        isOpen={isConfirmationModalOpen}
        title={'Confirmation'}
        body={`Are you sure you want to remove ${selectedItems.length === 1 ? 'image' : 'images'}?`}
        buttonText={'Remove'}
        buttonCancelText={'Cancel'}
        onButtonClick={this.onDeleteImagesClick}
        onCloseClick={this.hideConfirmationModal}
      />
    );
  }

  onDeleteImagesClick = () => {
    const { selectedItems } = this.state;
    const { user } = this.props;

    this.setState({
      isLoading: true,
      isConfirmationModalOpen: false,
    });

    const orderId = this.getOrderId();

    Promise.all(selectedItems.map(selectedItem => deleteOrderImage(user, orderId, selectedItem.id))).then(res => {
      this.setState({
        selectedItems: [],
        isSelectAllChecked: false,
      });

      this.setItems();
    });
  };

  onEditOrderImageClick = () => {
    this.setState({
      isOrderImageFormModalOpen: true,
    });
  };

  onCreateOrderImageClick = () => {
    this.setState({
      isOrderImageFormModalOpen: true,
    });
  };

  onCancelButtonClick = () => {
    this.setState({
      isOrderImageFormModalOpen: false,
    });
  };

  onSubmitForm = data => {
    const { selectedItems, total } = this.state;
    const { user } = this.props;

    const orderId = this.getOrderId();

    this.setState({
      isLoading: true,
    });

    const dataToTransfer = { ...data, quantity: Number(data.quantity) };

    if (selectedItems.length === 1) {
      const order = selectedItems[0];
      const { id } = order;

      updateOrderImage(user, orderId, id, dataToTransfer)
        .then(res => {
          this.setState({
            isOrderImageFormModalOpen: false,
            selectedItems: [res],
            isSelectAllChecked: false,
          });
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isOrderImageFormModalOpen: false,
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    } else {
      addOrderImage(user, orderId, dataToTransfer)
        .then(res => {
          const lastPage = getPagesCount(total + 1, LIMIT);
          this.setState({
            isOrderImageFormModalOpen: false,
            selectedItems: [res],
            isSelectAllChecked: false,
          });
          this.setCurrentPageParams(lastPage);
          this.setItems();
        })
        .catch(error => {
          const errorText = propz.get(error, ['response', 'data', 'details', 'text'], '');
          this.setState({
            isOrderImageFormModalOpen: false,
            isLoading: false,
            isErrorModalOpen: true,
            errorMessage: errorText,
          });
          console.error('error');
          console.error(errorText);
        });
    }
  };

  onTabClick = (event, index: number) => {
    event.preventDefault();

    this.setState({
      currentTabIndex: index,
    });
  };

  renderFormModal(): React.ReactNode {
    const { isOrderImageFormModalOpen, selectedItems } = this.state;
    const { user } = this.props;

    if (selectedItems.length === 1) {
      const orderImage = selectedItems[0];
      return (
        <SimpleModal isOpen={isOrderImageFormModalOpen}>
          <OrderImageForm
            onSubmit={this.onSubmitForm}
            onCancel={this.onCancelButtonClick}
            orderImage={orderImage}
            user={user}
          />
        </SimpleModal>
      );
    } else {
      return (
        <SimpleModal isOpen={isOrderImageFormModalOpen}>
          <OrderImageForm onSubmit={this.onSubmitForm} onCancel={this.onCancelButtonClick} user={user} />
        </SimpleModal>
      );
    }
  }

  onCloseErrorClick = () => {
    this.setState({
      isErrorModalOpen: false,
      errorMessage: '',
    });
  };

  renderSendingError(): React.ReactNode {
    const { errorMessage, isErrorModalOpen } = this.state;

    return (
      <SimpleModal
        isOpen={isErrorModalOpen}
        title={'Error'}
        body={errorMessage}
        buttonCancelText={'Ok'}
        onCloseClick={this.onCloseErrorClick}
      />
    );
  }

  getTabs(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;
    const { user } = this.props;

    const tabs = IMAGE_TABS;
    const currentTab = tabs[currentTabIndex];
    const [image] = selectedItems;

    switch (currentTab) {
      case TABS.SUMMARY:
        return <OrderImageSummary imageItem={image} user={user} />;
      default:
        return <div />;
    }
  }

  renderSummary(): React.ReactNode {
    const { selectedItems, currentTabIndex } = this.state;
    const isOneItemSelected = selectedItems.length === 1;

    if (!isOneItemSelected) {
      return <div className="font-weight-bold">Select an image above</div>;
    }

    const tabs = this.getTabs();

    return (
      <GenericSummary onTabClick={this.onTabClick} tabs={IMAGE_TABS} currentTabIndex={currentTabIndex}>
        {tabs}
      </GenericSummary>
    );
  }

  renderGrid(): React.ReactNode {
    const {
      items,
      sortDirection,
      sortColumnsName,
      isShowFilter,
      isDataFiltered,
      selectedItems,
      filters,
      isSelectAllChecked,
      currentPage,
      total,
      order,
    } = this.state;

    const actionItems = [
      {
        itemText: 'Create order image',
        onItemClick: this.onCreateOrderImageClick,
        isActive: selectedItems.length === 0,
      },
      {
        itemText: 'Edit order image',
        onItemClick: this.onEditOrderImageClick,
        isActive: selectedItems.length === 1,
      },
      {
        itemText: selectedItems.length === 1 ? 'Delete order image' : 'Delete order images',
        onItemClick: this.showConfirmationModal,
        isActive: selectedItems.length > 0,
      },
    ];

    const filterOptions = {};

    const gridTitle = typeof order !== 'undefined' ? `Order / Images` : '';

    return (
      <Grid
        dataItems={items}
        filters={filters}
        currentPage={currentPage}
        total={total}
        isSelectAllChecked={isSelectAllChecked}
        isDataFiltered={isDataFiltered}
        sortDirection={sortDirection}
        sortColumnsName={sortColumnsName}
        isShowFilter={isShowFilter}
        dataItemsSelected={selectedItems}
        columns={COLUMNS}
        actionItems={actionItems}
        options={filterOptions}
        onItemClick={this.onItemClick}
        onSortClick={this.onTableSortClick}
        onApplyFilterClick={this.onApplyFilterClick}
        onClearFilterClick={this.onClearFilterClick}
        onTableFilterChange={this.onTableFilterChange}
        onTableFilterClick={this.onTableFilterClick}
        setCurrentPageParams={this.setCurrentPageParams}
        onSelectAllClick={this.onSelectAllClick}
        onSelectAllOnPageClick={this.onSelectAllOnPageClick}
        onUnselectAllClick={this.onUnselectAllClick}
        gridTitle={gridTitle}
        goBack={this.goBack}
      />
    );
  }

  render() {
    const { isLoading, isConfirmationModalOpen, isOrderImageFormModalOpen } = this.state;

    if (isLoading) {
      return <Loader />;
    }

    const classes = isConfirmationModalOpen || isOrderImageFormModalOpen ? 'mt-3 modal-open' : 'mt-3';

    return (
      <div className={classes}>
        {this.renderConfirmationModal()}
        {this.renderFormModal()}
        {this.renderSendingError()}
        <div className="row">
          <div className="col-md-12">
            <ResizablePanel>
              {this.renderGrid()}
              {this.renderSummary()}
            </ResizablePanel>
          </div>
        </div>
      </div>
    );
  }
}
